/**
 * R12.1-FE: Geolocation adapter.
 * - NATIVE: @capacitor/geolocation (request permissions, get current position)
 * - WEB: navigator.geolocation with graceful fallback (no permission on HTTP, etc.)
 */

import { isNative } from './isNative';
import { Geolocation } from '@capacitor/geolocation';

export interface GeoPosition {
  latitude: number;
  longitude: number;
  accuracyMeters: number | null;
}

export class GeolocationError extends Error {
  constructor(
    message: string,
    public readonly code?: 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT' | 'UNSUPPORTED',
  ) {
    super(message);
    this.name = 'GeolocationError';
  }
}

/**
 * Request permission (native only; web will prompt on getCurrentPosition).
 */
export async function requestPermissions(): Promise<boolean> {
  if (isNative()) {
    const status = await Geolocation.checkPermissions();
    if (status.location === 'granted') return true;
    const request = await Geolocation.requestPermissions();
    return request.location === 'granted';
  }
  if (!navigator.permissions?.query) return true;
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state === 'granted' || result.state === 'prompt';
  } catch {
    return true;
  }
}

/**
 * Get current position. On web uses navigator.geolocation with high accuracy and timeout.
 * Throws GeolocationError on permission denied, unavailable, or timeout.
 */
export async function getCurrentPosition(): Promise<GeoPosition> {
  if (isNative()) {
    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    });
    return {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracyMeters: pos.coords.accuracy ?? null,
    };
  }

  if (!navigator.geolocation) {
    throw new GeolocationError('Geolocalizzazione non supportata da questo browser.', 'UNSUPPORTED');
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: position.coords.accuracy ?? null,
        });
      },
      (err: GeolocationPositionError) => {
        const code =
          err.code === 1
            ? 'PERMISSION_DENIED'
            : err.code === 2
              ? 'POSITION_UNAVAILABLE'
              : err.code === 3
                ? 'TIMEOUT'
                : undefined;
        const message =
          err.code === 1
            ? 'Permesso posizione negato. Abilita la posizione per timbrare.'
            : err.code === 2
              ? 'Impossibile ottenere la posizione. Riprova.'
              : err.code === 3
                ? 'Timeout lettura GPS. Riprova.'
                : err.message || 'Errore geolocalizzazione';
        reject(new GeolocationError(message, code));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });
}
